/**
 * Attention Required Activities Hook
 *
 * React Query hook for fetching activities that require human attention.
 * Used by AttentionDetail component in the Control Hub.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { ActivitiesListResponse } from "@/data/api/activities";

// ============================================================================
// Query Keys
// ============================================================================

export const ATTENTION_REQUIRED_QUERY_KEYS = {
    all: ["attention-required"] as const,
    list: (limit: number, startDate?: string | null, endDate?: string | null) =>
        [...ATTENTION_REQUIRED_QUERY_KEYS.all, "list", limit, startDate, endDate] as const,
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to fetch activities that require attention
 *
 * @param limit - Maximum number of activities to return (default: 10)
 * @param startDate - Start date filter (YYYY-MM-DD)
 * @param endDate - End date filter (YYYY-MM-DD)
 * @param enabled - Whether the query should be enabled (default: true)
 *
 * Returns activities with requires_attention=true, plus attention_count.
 */
export function useAttentionRequired(
    limit: number = 10,
    startDate?: string | null,
    endDate?: string | null,
    enabled: boolean = true,
) {
    return useQuery({
        queryKey: ATTENTION_REQUIRED_QUERY_KEYS.list(limit, startDate, endDate),
        queryFn: async (): Promise<ActivitiesListResponse> => {
            const params: Record<string, string | number> = { limit };
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            return await apiGet<ActivitiesListResponse>(
                "/activities/attention-required",
                { params },
            );
        },
        enabled,
        staleTime: 15 * 1000,
        refetchInterval: 30 * 1000,
    });
}
